/* import __COLOCATED_TEMPLATE__ from './phone-number-purchase-section.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get, post } from 'embercom/lib/ajax';
import { PhoneNumberType } from 'embercom/models/settings/calling';
import type IntlService from 'embercom/services/intl';
import type CallingPhoneNumber from 'embercom/models/calling-phone-number';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';

interface Signature {
  Element: HTMLDivElement;
}

export default class PhoneNumberPurchaseSection extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare store: any;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked isLoading = false;
  @tracked selectedPhoneNumber?: string;

  @action
  async purchaseNumber() {
    await taskFor(this.fetchAndPurchaseNumber).perform();
    await this.finVoiceConfigService.loadAllData();
  }

  @dropTask
  *fetchAndPurchaseNumber(): TaskGenerator<void> {
    this.isLoading = true;

    try {
      // First, fetch available phone numbers
      let availableNumbers: string[] = yield get(
        `/ember/calling_settings/fetch_available_phone_numbers`,
        {
          app_id: this.appService.app.id,
          country: 'US',
          phone_type: PhoneNumberType.Local,
          start_with: '',
        },
      );

      if (!availableNumbers || availableNumbers.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t('operator.fin.setup.phone-purchase.no-numbers-available'),
        );
        return;
      }

      // Select the first available number
      this.selectedPhoneNumber = availableNumbers[0];

      // Try to fetch pricing info (backend may require this for some environments)
      try {
        yield get(`/ember/calling_settings/fetch_phone_number_price`, {
          app_id: this.appService.app.id,
          country: 'US',
          phone_type: PhoneNumberType.Local,
        });
      } catch (pricingError) {
        // If pricing data is not available (e.g., development environment), continue anyway
        console.warn('Pricing data not available, continuing with provisioning:', pricingError);
      }

      // Purchase the selected number
      let response: CallingPhoneNumber = yield post(
        `/ember/calling_settings/provision_phone_number`,
        {
          app_id: this.appService.app.id,
          country: 'US',
          phone_type: PhoneNumberType.Local,
          selected_phone_number: this.selectedPhoneNumber,
        },
      );

      // Update the store with the new phone number
      this.store.pushPayload({ 'calling-phone-number': response });
      let phoneNumber = this.store.peekRecord('calling-phone-number', response.id);

      // Configure the phone number for workflow routing and inbound calls
      if (phoneNumber) {
        phoneNumber.inboundEnabled = true;
        phoneNumber.routingMethod = 'workflow';
        yield phoneNumber.save();
      }

      // Refresh the number configurations so the new number appears in the UI
      this.finVoiceConfigService.numberConfigurationsLoaded = false;

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.phone-purchase.success', {
          phoneNumber: this.selectedPhoneNumber,
        }),
      );
      // Reset state
      this.selectedPhoneNumber = undefined;
    } catch (error: any) {
      let errorMessage =
        error?.jqXHR?.responseJSON?.errors ||
        this.intl.t('operator.fin.setup.phone-purchase.error');

      this.notificationsService.notifyError(errorMessage);

      // Reset selected number on error
      this.selectedPhoneNumber = undefined;

      throw error;
    } finally {
      this.isLoading = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::PhoneNumberPurchaseSection': typeof PhoneNumberPurchaseSection;
  }
}
